/**
 * Módulo del tutorial (grupo "El negocio"): Compra y venta de hacienda (KAN-232).
 *
 * El ciclo completo: desde una sesión de carga se registra la compra o la
 * venta — por kilo o por cabeza, con desvaste — y el stock, el valúo y los
 * reportes se acomodan solos. Cierre en Operaciones, el histórico de todo
 * lo comprado y vendido.
 *
 * Anclas: menu-sesiones, sesion-card-compra/venta (card de sesión),
 * compra-pesos-faltantes (tabla de pesos, abajo del modal), compra-peso-total,
 * compra-desvaste, compra-form-cantidad/precio, btn-save-compra (modal de compra),
 * menu-operaciones-compra-venta (menú Finanzas).
 */

import type { TutorialModuleDef } from '../../../services/tutorial/types';
import { getRepositories } from '../../../services/storage';

export const compraVentaModule: TutorialModuleDef = {
  id: 'compra-venta',
  title: 'Compra y venta de hacienda',
  description: 'Registrá compras y ventas desde tus sesiones — por kilo o por cabeza, con desvaste — y el stock y el valúo se acomodan solos.',
  emoji: '🤝',
  image: 'images/tutorial/compra-venta.png',
  permission: { module: 'financial', action: 'create' },
  grupo: 'negocio',
  route: '/sesiones',
  steps: [
    {
      id: 'concepto-compra-venta',
      route: '/tutorial',
      text: 'Cargás, criás… y un día vendés o comprás. Registrado acá, el stock, el valúo y los reportes siguen solos: nada de anotar en un cuaderno.',
      narration:
        'Vamos a cerrar el círculo del negocio: comprás animales, los criás, y un buen día los vendés. Si eso queda en un cuaderno, a fin de año nadie sabe qué pasó. Registrado acá, el stock se acomoda solo, el valúo se actualiza solo, y los reportes te dicen cómo te fue. Vos sólo ponés el precio.',
    },
    {
      id: 'ir-a-sesiones-cv',
      target: '[data-ar-guide="menu-sesiones"]',
      label: 'Sesiones (menú Ganado)',
      text: 'Las compras y ventas se cargan sobre una sesión: la de la compra nueva, o la sesión donde están los animales que vas a vender. Andá a "Sesiones" en el menú Ganado.',
      narration:
        'La compra y la venta siempre viven dentro de una sesión. Si compraste hacienda, se carga en la sesión de esa compra. Si vendiste, buscás la sesión donde están esos animales. Andá al menú, sección Ganado, y tocá Sesiones.',
    },
    {
      id: 'boton-compra',
      route: '/sesiones',
      target: '[data-ar-guide="sesion-card-compra"]',
      label: 'Cargar Compra',
      text: 'En la card de la sesión tocá "Compra" (para vender, es el botón de al lado: "Venta"). Se abre el formulario de la operación.',
      narration:
        'En la card de cada sesión tenés los dos botones: Compra y Venta, manita a manita. Tocá Compra y se abre el formulario de la operación. El de Venta funciona igual, así que si aprendés uno, ya sabés los dos.',
    },
    {
      id: 'elegir-animales-peso',
      route: '/sesiones',
      target: '[data-ar-guide="compra-seleccion-animales"]',
      advanceTargets: ['[data-ar-guide="compra-seleccion-animales"] label'],
      label: 'Lista de animales de la operación',
      text: 'En esta lista elegís los animales de la operación: tocá los casilleros de los que entran.',
      narration:
        'Adentro del formulario elegís qué animales entran a la operación, tocándolos de esta lista. Los marcados como comprados ya vienen señalados; el resto lo sumás o lo sacás con un toque.',
    },
    {
      id: 'peso-de-animales',
      route: '/sesiones',
      target: '[data-ar-guide="compra-pesos-faltantes"]',
      label: 'Tabla "Peso de Animales" (abajo del todo)',
      advanceOnClick: false,
      text: 'Si a algún animal le falta el peso, aparece esta tabla "Peso de Animales" ABAJO DEL TODO del formulario: cargá ahí el peso de compra de cada uno. Sin esos pesos, la compra no se deja guardar.',
      narration:
        'Ahora, un detalle que se le escapa a más de uno: si algún animal no tiene su peso cargado, abajo del todo del formulario aparece una tabla que se llama Peso de Animales. Ahí tenés que anotarle el peso de compra a cada uno que falte. Y no te apures a tocar Guardar sin eso, porque la app no te deja: sin peso no hay cuenta clara.',
    },
    {
      id: 'tipo-de-precio',
      route: '/sesiones',
      target: '[data-ar-guide="compra-tipo-precio"]',
      label: 'Tipo de Compra (por kilo/unidad)',
      advanceOnClick: false,
      text: 'Marcá cómo se paga: "Comprar por Kilo" o "por Unidad". Con el precio por kilo, el peso manda; por unidad, contás animales y listo.',
      narration:
        'Ahora decidí cómo se pactó el negocio: por kilo o por cabeza. Por kilo, el peso es el que manda y todo se calcula de ahí. Por cabeza, más simple: contás los animales, ponés el precio de cada uno, y listo. Como en el mercado, pero sin apurarse el remate.',
    },
    {
      id: 'peso-total',
      route: '/sesiones',
      target: '[data-ar-guide="compra-peso-total"]',
      label: 'Peso Total (con desvaste opcional)',
      advanceOnClick: false,
      text: 'El "Peso Total" viene precargado con la suma de los pesos de los animales: al lado te la muestra ("Total con ultimos pesajes"). Podés ajustarlo a mano, y si te descontaron desvaste, marcá "Asignar desvaste manualmente" y poné el %.',
      narration:
        'Mirá esto: el campo Peso Total ya viene con la suma de los pesos de los animales, y al lado te la muestra clara: total con últimos pesajes. Si en la balanza te dieron otro número, lo ajustas a mano nomás. ¿Y si te descontaron desvaste? Marcá asignar desvaste manualmente, poné el porcentaje, y la cuenta se hace derecha sola.',
    },
    {
      id: 'cantidad-y-precio',
      route: '/sesiones',
      target: '[data-ar-guide="compra-form-cantidad"]',
      label: 'Peso Total Desvastado',
      advanceOnClick: false,
      text: '¿Ya tenés el peso final de la boleta? Cargalo directo en "Peso Total Desvastado" — la app calcula sola el desvaste contra el peso total.',
      narration:
        'Y si ya venís con el peso final de la boleta en la mano, el desvastado, no hace falta dar vueltas: cargalo directo acá en Peso Total Desvastado, y la app saca sola el desvaste contra el peso total. Nada de cuentas a mano en el borde de la boleta.',
    },
    {
      id: 'precio-compra',
      route: '/sesiones',
      target: '[data-ar-guide="compra-form-precio"]',
      label: 'Precio de compra',
      advanceOnClick: false,
      text: 'El precio, acá al lado: por kilo o por unidad. Con el peso, es lo único obligatorio — el resto (IVA, comisión, flete, otros gastos) es opcional, por si lo querés completar.',
      narration:
        'Y el precio va al lado: por kilo o por unidad, según lo que marcaste. Y quedate tranquilo: con el peso y este precio ya tenés todo lo obligatorio. El resto, el IVA, la comisión, el flete, otros gastos, todo eso es opcional: está por si lo querés anotar, pero la compra sale sin tocarlo.',
    },
    {
      id: 'guardar-compra',
      route: '/sesiones',
      target: '[data-ar-guide="btn-save-compra"]',
      label: 'Guardar compra',
      text: 'Revisá el total y tocá "Guardar". El stock queda actualizado al instante — y la venta se carga igual desde el botón "Venta" de la card.',
      narration:
        'Revisá el total, y cuando todo cierra, tocá Guardar. El stock queda actualizado en el momento: los animales entran sin que tengas que tocar nada más. Y recordá que la venta se hace igual, desde el botón Venta de la card.',
    },
    {
      id: 'operaciones-historico',
      route: '/operaciones-compra-venta',
      target: '[data-ar-guide="menu-operaciones-compra-venta"]',
      label: 'Compra y Venta (menú Finanzas)',
      text: 'Y todo el histórico queda en "Compra y Venta", el acceso del menú Finanzas justo debajo de Administración: qué compraste, qué vendiste y a qué precio — la memoria del negocio.',
      narration:
        'Y para que nada se pierda, te queda el histórico completo en Compra y Venta, el acceso nuevo del menú, en Finanzas, justo debajo de Administración: cada compra, cada venta, con sus precios y sus fechas. La memoria del negocio, siempre a mano. Ahora sí: a comprar bien y vender mejor, criollo.',
    },
  ],
  completionCheck: async () => {
    const operaciones = (await getRepositories().operacionCompraVenta.getAllForActiveEmpresa()) as unknown[];
    return operaciones.length > 0;
  },
};
